import { useState } from 'react';
import { ChefHat, Eye, Receipt, Repeat } from 'lucide-react';
import {
  Bar,
  CHART_COLORS,
  DashboardRow,
  DataTable,
  FilterBar,
  FilterDivider,
  MetricCard,
  MetricRow,
  SelectableChip,
  type DataTableColumn,
} from '../../components';
import { formatCents, formatCount } from '../../lib/format/money';
import { formatDate } from '../../lib/format/dates';
import { LocalePicker } from '../locales/LocalePicker';
import { humanize } from '../profiles/people';
import type { CohortRow, ListingOutcomes, TopPerformer } from './api';
import { QueryCard, SeriesPair, WindowControls, useAnalyticsWindow } from './parts';
import {
  useAvgOrderValue,
  useChefActivityTrends,
  useCohortRetention,
  useDishSeries,
  useFunnel,
  useListingOutcomes,
  useMostViewedCuisines,
  useMostViewedDishes,
  useRepeatPurchaseRate,
  useSignupSeries,
  useTopOrderedCuisines,
  useTopPerformers,
} from './queries';

const STAGE_LABEL: Record<string, string> = {
  visits: 'Visited (devices + signed in)',
  signup: 'Signed up',
  viewed_listing: 'Viewed a listing',
  first_order_or_publish: 'First order or publish',
  repeat: 'Repeat',
};

const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : '—');
const pctText = (v: number) => `${v.toFixed(1)}%`;
const pctOrDash = (v: number | null) => (v === null ? '—' : pctText(v));

/**
 * Growth & Cohorts (kit `AnalyticsScreen`; FE plan §7). Range, grain and
 * locales live in the URL and drive every card's startDate / endDate / bucket
 * / localeIds.
 */
export function GrowthScreen() {
  const a = useAnalyticsWindow();
  const w = a.window;

  const chefSignups = useSignupSeries('chef', w);
  const eaterSignups = useSignupSeries('eater', w);
  const listed = useDishSeries('listed', w);
  const sold = useDishSeries('sold', w);
  const funnel = useFunnel(w);
  const [cohortType, setCohortType] = useState<'chef' | 'eater'>('eater');
  const cohorts = useCohortRetention(cohortType, a.bucket === 'day' ? 'week' : a.bucket, w);
  const trends = useChefActivityTrends(w);
  const performers = useTopPerformers(w);
  const repeat = useRepeatPurchaseRate(w);
  const aov = useAvgOrderValue(w);
  const outcomes = useListingOutcomes(w);
  const o = outcomes.data?.outcomes;
  const oValue = (n: (x: ListingOutcomes) => number) => (o ? formatCount(n(o)) : outcomes.isError ? '—' : '…');
  const viewedDishes = useMostViewedDishes(w);
  const viewedCuisines = useMostViewedCuisines(w);
  const orderedCuisines = useTopOrderedCuisines(w);

  const noun = a.bucket;

  return (
    <div className="flex flex-col gap-[18px]">
      <FilterBar>
        <WindowControls w={a} />
        <FilterDivider />
        <LocalePicker value={a.localeIds} onChange={a.setLocaleIds} />
        {a.timeZone && <span className="text-xs text-muted">Dates in {a.timeZone}</span>}
      </FilterBar>

      <DashboardRow cols="minmax(0,1fr) minmax(0,1fr)" height="default">
        <QueryCard eyebrow="Growth" title={`New signups per ${noun}`} query={chefSignups}>
          {(chefs) =>
            eaterSignups.data ? (
              <>
                <SeriesPair
                  a={chefs.series}
                  b={eaterSignups.data.series}
                  aLabel="New chefs"
                  bLabel="New eaters"
                  bucket={a.bucket}
                  height={170}
                />
              </>
            ) : null
          }
        </QueryCard>
        <QueryCard eyebrow="Growth" title={`Dishes per ${noun}`} query={listed}>
          {(l) =>
            sold.data ? (
              <>
                <SeriesPair
                  a={l.series}
                  b={sold.data.series}
                  aLabel="Listed"
                  bLabel="Portions sold"
                  bucket={a.bucket}
                  height={170}
                />
              </>
            ) : null
          }
        </QueryCard>
      </DashboardRow>

      <DashboardRow cols="minmax(0,1fr) minmax(0,1.1fr)" height="tall">
        <QueryCard eyebrow="Marketplace funnel" title="Drop-off by stage" query={funnel}>
          {(f) => {
            // Percentages are of SIGNUPS (the profile population). `visits` counts
            // browsers — a different unit — so it shows its count only.
            const signups = f.stages.find((s) => s.stage === 'signup')?.count ?? f.stages[0]?.count ?? 0;
            const widest = Math.max(1, ...f.stages.map((s) => s.count));
            return (
              <>
                <div className="flex flex-col gap-2.5">
                  {f.stages.map((s, i) => (
                    <div key={s.stage}>
                      <div className="mb-[5px] flex justify-between text-[13px]">
                        <span className="text-ink">{STAGE_LABEL[s.stage] ?? humanize(s.stage)}</span>
                        <span className="text-muted tabular-nums">
                          {formatCount(s.count)}
                          {s.stage !== 'visits' && ` · ${pct(s.count, signups)}`}
                          {s.dropOffPctFromPrev !== null && (
                            <span className="ml-2 text-[#B23A2E]">−{Math.round(s.dropOffPctFromPrev)}%</span>
                          )}
                        </span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-pill bg-[rgba(143,68,32,.08)]">
                        <div
                          className="h-full rounded-pill"
                          style={{
                            width: `${(s.count / widest) * 100}%`,
                            background: CHART_COLORS[i % CHART_COLORS.length],
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {!!f.missingStages?.length && (
                  <p className="m-0 mt-3.5 text-xs text-muted">
                    Not measured yet: {f.missingStages.map(humanize).join(', ')}.
                  </p>
                )}
              </>
            );
          }}
        </QueryCard>

        <QueryCard
          eyebrow="Cohort retention"
          title="7 / 30 / 90-day retention"
          query={cohorts}
          actions={
            <div className="flex gap-1.5" role="group" aria-label="Cohort type">
              {(['eater', 'chef'] as const).map((t) => (
                <SelectableChip key={t} active={cohortType === t} onClick={() => setCohortType(t)}>
                  {t === 'eater' ? 'Eaters' : 'Chefs'}
                </SelectableChip>
              ))}
            </div>
          }
        >
          {(c) => (
            <>
              <DataTable
                label="Cohort retention"
                compact
                minWidth={0}
                columns={cohortColumns(a.bucket === 'month' ? 'month' : 'week')}
                rows={c.cohorts ?? []}
                getRowId={(r) => r.cohortStart}
                emptyLabel="No cohorts in this range."
              />
              {trends.data && (
                <div className="mt-4 border-t border-[rgba(143,68,32,.1)] pt-3.5">
                  <div className="mb-2.5 text-[11px] font-bold uppercase tracking-[.14em] text-muted">
                    Definitions in force
                  </div>
                  <div className="flex flex-col gap-[7px] text-[13px] leading-normal text-[rgba(42,22,9,.72)]">
                    <div>
                      <strong className="text-ink">Chef active</strong> — published within{' '}
                      {trends.data.trends.activeWithinDays} days
                    </div>
                    <div>
                      <strong className="text-ink">Chef gone</strong> — nothing published in{' '}
                      {trends.data.trends.goneAfterDays} days
                    </div>
                    {performers.data?.items?.[0] && (
                      <div>
                        <strong className="text-ink">Top performer</strong> — measured over a rolling{' '}
                        {performers.data.items[0].windowDays} days
                      </div>
                    )}
                    {repeat.data && (
                      <div>
                        <strong className="text-ink">Repeat purchaser</strong> — more than one order in{' '}
                        {repeat.data.stats.windowDays} days
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </QueryCard>
      </DashboardRow>

      <MetricRow>
        <MetricCard
          label="Avg. order value"
          value={aov.data ? formatCents(aov.data.avgOrderValue.avgChargeTotalCents) : aov.isError ? '—' : '…'}
          hint={
            aov.data
              ? `${formatCents(aov.data.avgOrderValue.avgGrossCents)} to the chef · ${formatCount(aov.data.avgOrderValue.transactions)} sales`
              : undefined
          }
          accent="var(--admin-chart-1)"
          icon={<Receipt size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Repeat purchase rate"
          value={repeat.data ? pctText(repeat.data.stats.repeatPurchasePct) : repeat.isError ? '—' : '…'}
          hint={
            repeat.data
              ? `${formatCount(repeat.data.stats.repeatPurchasers)} of ${formatCount(repeat.data.stats.eatersWithOrders)} ordering eaters · rolling ${repeat.data.stats.windowDays} days`
              : undefined
          }
          accent="var(--admin-chart-2)"
          icon={<Repeat size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Chefs active"
          value={trends.data ? formatCount(trends.data.trends.activeChefs) : trends.isError ? '—' : '…'}
          hint={
            trends.data
              ? `${formatCount(trends.data.trends.inactiveChefs)} inactive · ${formatCount(trends.data.trends.goneChefs)} gone`
              : undefined
          }
          accent="var(--admin-chart-3)"
          icon={<ChefHat size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Viewed, not ordered"
          value={oValue((x) => x.listingsViewedNotOrdered)}
          hint={
            !o
              ? undefined
              : o.listingsViewed === 0
                ? 'No viewed listings have closed in this range yet'
                : `${pctOrDash(o.viewedNotOrderedPct)} of ${formatCount(o.listingsViewed)} viewed listings · counted at close`
          }
          accent="var(--admin-chart-4)"
          icon={<Eye size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <DashboardRow cols="repeat(4, minmax(0,1fr))" height="auto">
        <QueryCard eyebrow="Eater behaviour" title="Most viewed dishes" query={viewedDishes} scroll={false}>
          {(d) => (
            <BarList
              rows={(d.items ?? []).map((r) => ({
                key: r.listingId,
                label: r.title || 'Untitled',
                value: r.uniqueViewers,
                title: `${formatCount(r.uniqueViewers)} signed-in viewers · ${formatCount(r.anonymousViewers ?? 0)} signed-out devices · ${formatCount(r.ordersPlaced)} orders (${pctText(r.viewToOrderPct)}) · by ${r.listerName}`,
              }))}
              empty="No identified views in this range."
            />
          )}
        </QueryCard>
        <QueryCard eyebrow="Eater behaviour" title="Most viewed cuisines" query={viewedCuisines} scroll={false}>
          {(d) => (
            <BarList
              rows={(d.items ?? []).map((r) => ({
                key: r.tag,
                label: humanize(r.tag),
                value: r.uniqueViewers,
                title: `${formatCount(r.uniqueViewers)} signed-in viewers · ${formatCount(r.anonymousViewers ?? 0)} signed-out devices · ${formatCount(r.portionsSold)} portions sold (${pctText(r.viewToOrderPct)})`,
              }))}
              empty="No identified views in this range."
            />
          )}
        </QueryCard>
        <QueryCard eyebrow="Eater behaviour" title="Browse without order" query={outcomes} scroll={false}>
          {({ outcomes: x }) => (
            <Figure
              value={formatCount(x.eaterDaysWithoutOrder)}
              hint={`${x.browseWithoutOrderPct === null ? '' : `${pctText(x.browseWithoutOrderPct)} `}of ${formatCount(x.eaterDaysBrowsed)} browsing days · ${formatCount(x.eatersWithoutOrder)} eaters`}
            />
          )}
        </QueryCard>
        <QueryCard eyebrow="Eater behaviour" title="Abandoned" query={outcomes} scroll={false}>
          {({ outcomes: x }) => (
            <Figure
              value={formatCount(x.ordersAbandoned)}
              hint={`${x.abandonedPct === null ? '' : `${pctText(x.abandonedPct)} `}of ${formatCount(x.ordersStarted)} checkouts started`}
            />
          )}
        </QueryCard>
      </DashboardRow>

      <DashboardRow cols="minmax(0,1fr) minmax(0,1.2fr)" height="auto">
        <QueryCard eyebrow="Eater behaviour" title="Top ordered cuisines" query={orderedCuisines} scroll={false}>
          {(d) => (
            <BarList
              rows={(d.items ?? []).map((r) => ({
                key: `${r.tagType}:${r.tag}`,
                label: humanize(r.tag),
                value: r.portions,
                title: `${formatCount(r.portions)} portions · ${formatCount(r.listings)} listings`,
              }))}
              empty="No orders in this range."
            />
          )}
        </QueryCard>
        <QueryCard eyebrow="Chefs" title="Top performers" query={performers} scroll={false}>
          {(d) => (
            <DataTable
              label="Top performers"
              compact
              minWidth={0}
              columns={PERFORMER_COLUMNS}
              rows={d.items ?? []}
              getRowId={(r) => r.profileId}
              emptyLabel="No chef meets both thresholds yet."
            />
          )}
        </QueryCard>
      </DashboardRow>
    </div>
  );
}

/** A single headline number inside a panel card, with its hint below. */
function Figure({ value, hint }: { value: string; hint: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="font-heading text-[32px] font-extrabold leading-[1.1] tracking-[-.02em] text-brown-deep tabular-nums">
        {value}
      </div>
      <div className="text-[13px] text-muted">{hint}</div>
    </div>
  );
}

function cohortColumns(bucket: 'week' | 'month'): DataTableColumn<CohortRow>[] {
  const rate = (n: number, r: CohortRow) => (
    <span title={`${formatCount(n)} of ${formatCount(r.size)}`}>{pct(n, r.size)}</span>
  );
  return [
    {
      id: 'cohort',
      header: bucket === 'month' ? 'Month cohort' : 'Week of',
      width: '1.6fr',
      cell: (r) => <span className="font-semibold">{formatDate(r.cohortStart, 'UTC')}</span>,
    },
    { id: 'size', header: 'Size', width: '70px', align: 'right', numeric: true, cell: (r) => formatCount(r.size) },
    { id: 'd7', header: '7 day', width: '1fr', align: 'right', numeric: true, cell: (r) => rate(r.day7Active, r) },
    { id: 'd30', header: '30 day', width: '1fr', align: 'right', numeric: true, cell: (r) => rate(r.day30Active, r) },
    { id: 'd90', header: '90 day', width: '1fr', align: 'right', numeric: true, cell: (r) => rate(r.day90Active, r) },
  ];
}

const PERFORMER_COLUMNS: DataTableColumn<TopPerformer>[] = [
  {
    id: 'chef',
    header: 'Chef',
    width: '1.6fr',
    cell: (r) => <span className="truncate font-semibold">{r.displayName}</span>,
  },
  {
    id: 'published',
    header: 'Published',
    width: '90px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.dishesPublished),
  },
  {
    id: 'ordered',
    header: 'Ordered',
    width: '80px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.dishesOrdered),
  },
  {
    id: 'rate',
    header: 'Order rate',
    width: '96px',
    align: 'right',
    numeric: true,
    cell: (r) => pctText(r.orderRatePct),
  },
];

function BarList({
  rows,
  empty,
}: {
  rows: Array<{ key: string; label: string; value: number; title?: string }>;
  empty: string;
}) {
  if (!rows.length) return <div className="text-[13px] text-muted">{empty}</div>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="flex flex-col gap-[11px]">
      {rows.map((r, i) => (
        <div key={r.key} title={r.title}>
          <Bar
            label={r.label}
            value={formatCount(r.value)}
            pct={(r.value / max) * 100}
            color={CHART_COLORS[i % CHART_COLORS.length]}
            labelWidth={160}
          />
        </div>
      ))}
    </div>
  );
}
